import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'

import { FieldError } from '@/components/feedback/FieldError'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { groupInfoSchema, groupInfoValues, type GroupInfoValues } from '@/features/admin/group-form'
import { fieldErrorProps } from '@/lib/form/field-error'
import { useRecordEditor } from '@/lib/form/use-record-editor'
import { messages } from '@/lib/messages'
import { ApiError, toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'

const FORM_ID = 'create-group-form'

/** Yeni grup: ad ve açıklama. Yeni kayıt olduğu için sürüm yoktur; kirliyken kapatma onay sorar. */
export function CreateGroupDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const form = useForm<GroupInfoValues>({
    resolver: zodResolver(groupInfoSchema),
    defaultValues: groupInfoValues(null),
  })

  function close() {
    form.reset(groupInfoValues(null))
    onOpenChange(false)
  }

  const editor = useRecordEditor({
    form,
    submit: (values) =>
      services.admin.createGroup({ name: values.name, description: values.description || null }),
    onSaved: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.groups() })
      close()
    },
    // Aynı adlı grup 409 `group_name_taken` döner; mesaj adın altında görünür.
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'group_name_taken') {
        form.setError(
          'name',
          { type: 'server', message: toProblem(error).detail ?? error.message },
          { shouldFocus: true },
        )
        return true
      }
      return false
    },
  })

  const errors = form.formState.errors

  return (
    <Dialog open={open} onOpenChange={(next) => !next && editor.requestClose(close)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.groups.createTitle}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {editor.dialog}
          <form
            id={FORM_ID}
            className="flex flex-col gap-3"
            noValidate
            onSubmit={(event) => void editor.save(event)}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-group-name">{messages.groups.name}</Label>
              <Input
                id="new-group-name"
                {...fieldErrorProps('new-group-name', errors.name?.message)}
                {...form.register('name')}
              />
              <FieldError id="new-group-name" message={errors.name?.message} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-group-description">{messages.groups.descriptionField}</Label>
              <Input
                id="new-group-description"
                {...fieldErrorProps('new-group-description', errors.description?.message)}
                {...form.register('description')}
              />
              <FieldError id="new-group-description" message={errors.description?.message} />
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => editor.requestClose(close)}>
            {messages.common.cancel}
          </Button>
          <Button type="submit" form={FORM_ID} disabled={editor.saving}>
            {messages.common.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
